import { FC, memo } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  Browser,
  ChevronRight,
} from "@hl/base-components/lib/assets/icons.generated";
import { Box, Space, Text } from "@mantine/core";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "~/../../shared-features/lib/features/auth/hooks/useFeatureFlags";

import { ReactComponent as TokenIcon } from "assets/icons/hexagon.svg";
import AnchorItem from "components/EntityList/AnchorItem";
import { TOOLS_URL } from "~/config";

import { useGatedEntitiesQuery } from "./queries/gates.graphql.generated";

export type Props = {
  gateId: string;
  showLabel?: boolean;
};

const GatedEntities: FC<Props> = ({ gateId, showLabel = true }) => {
  const showGatedMintVectors = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_GATED_MINTS
  );

  const { data } = useGatedEntitiesQuery({ variables: { gateId } });
  if (!data) {
    return <></>;
  }

  if (!data.gatedEntities.length) {
    return <Text>Nothing is linked to this gate</Text>;
  }

  return (
    <Box>
      {showLabel && (
        <Text size="sm" fw={500}>
          Items linked to this gate
        </Text>
      )}
      <Space h={8}></Space>
      <BoxContainer py={0}>
        {data.gatedEntities.map((entity, index) => {
          if (entity.__typename === "GatedPage") {
            return (
              <AnchorItem
                key={entity.pageId}
                useRouterLink
                href={TOOLS_URL.pages.edit.replace(":pageId", entity.pageId)}
                iconLeft={<Browser />}
                iconRight={<ChevronRight />}
                showDivider={index !== data.gatedEntities.length - 1}
              >
                <Text size="sm">
                  {entity.__typename === "GatedPage"
                    ? entity.name.length > 18
                      ? entity.name.slice(0, 18) + "..."
                      : entity.name
                    : ""}
                </Text>
              </AnchorItem>
            );
          }

          if (entity.__typename === "GatedMintVector") {
            if (!showGatedMintVectors) return <></>;
            return (
              <AnchorItem
                key={`gated-mint-vector-${index}`}
                useRouterLink
                href={TOOLS_URL.collections.manage.replace(
                  ":collectionId",
                  entity.collectionId ?? ""
                )}
                iconLeft={<TokenIcon />}
                iconRight={<ChevronRight />}
                showDivider={index !== data.gatedEntities.length - 1}
              >
                <Text size="sm">
                  {entity.__typename === "GatedMintVector"
                    ? entity.editionName.length > 18
                      ? entity.editionName.slice(0, 18) + "..."
                      : entity.editionName
                    : ""}
                </Text>
              </AnchorItem>
            );
          }

          if (entity.__typename === "GatedAuction") {
            return (
              <AnchorItem
                key={`gated-auction-${index}`}
                useRouterLink
                href={TOOLS_URL.collections.manage.replace(
                  ":collectionId",
                  entity.auctionCollectionId
                )}
                iconLeft={<TokenIcon />}
                iconRight={<ChevronRight />}
                showDivider={index !== data.gatedEntities.length - 1}
              >
                <Text size="sm">
                  {entity.editionName.length > 18
                    ? entity.editionName.slice(0, 18) + "..."
                    : entity.editionName}
                </Text>
              </AnchorItem>
            );
          }

          return <></>;
        })}
      </BoxContainer>
    </Box>
  );
};

export default memo(GatedEntities);
